import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Cloud, RefreshCw, AlertTriangle, Type, Image } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Toggle LLM compact pour intégration dans les pages de service
 * Usage: <ServiceLLMToggle serviceId="tribunes" />
 * Visible uniquement pour les amiraux
 * 
 * Affiche DEUX toggles:
 * - Texte (LLM): Emergent (Claude/Gemini) ou Direct (Anthropic/OpenAI)
 * - Images: Emergent (Gemini) ou Direct (OpenAI gpt-image-1.5)
 */

const SERVICE_NAMES = {
  create: 'Créer (articles)',
  tribunes: 'Auto Tribunes',
  cp: 'Auto CP',
  dossier: 'Auto Dossier',
  interviews: 'Auto Interviews',
  global: 'Global'
};

const ServiceLLMToggle = ({ serviceId, className = '' }) => {
  // États pour le mode texte
  const [textMode, setTextMode] = useState(null);
  // États pour le mode image
  const [imageMode, setImageMode] = useState(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [userGrade, setUserGrade] = useState(null);
  
  // États pour le dialogue de confirmation
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingChange, setPendingChange] = useState(null); // { type: 'text' | 'image', newMode: string }

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const fetchModes = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Fetch text modes
      const textResponse = await fetch(`${API_BASE}/api/admin/llm-modes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (textResponse.ok) {
        const data = await textResponse.json();
        setTextMode(data.modes?.[serviceId] || 'emergent');
      }
      
      // Fetch image modes
      const imageResponse = await fetch(`${API_BASE}/api/admin/image-modes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (imageResponse.ok) {
        const data = await imageResponse.json();
        setImageMode(data.modes?.[serviceId] || 'emergent');
      }
    } catch (err) {
      console.error('Erreur chargement modes LLM:', err);
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE, serviceId]);

  useEffect(() => {
    const checkGrade = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setIsLoading(false);
          return;
        }
        
        const response = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const user = await response.json();
          setUserGrade(user.grade);
          
          if (user.grade === 'amiral') {
            fetchModes();
          } else {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[LLMToggle] Erreur vérification grade:', err);
        setIsLoading(false);
      }
    };
    
    checkGrade();
  }, [API_BASE, fetchModes]);

  const handleToggleClick = (type) => {
    const currentMode = type === 'text' ? textMode : imageMode;
    const newMode = currentMode === 'emergent' ? 'direct' : 'emergent';
    setPendingChange({ type, newMode });
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!pendingChange) return;
    
    setShowConfirm(false);
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = pendingChange.type === 'text' 
        ? `${API_BASE}/api/admin/llm-modes/${serviceId}`
        : `${API_BASE}/api/admin/image-modes/${serviceId}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode: pendingChange.newMode })
      });
      
      if (response.ok) {
        if (pendingChange.type === 'text') {
          setTextMode(pendingChange.newMode);
        } else {
          setImageMode(pendingChange.newMode);
        }
      }
    } catch (err) {
      console.error('Erreur changement mode:', err);
    } finally {
      setIsLoading(false);
      setPendingChange(null);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setPendingChange(null);
  };

  // Attendre de savoir si l'utilisateur est amiral
  if (isLoading && userGrade === null) {
    return null;
  }
  
  // Ne rien afficher si l'utilisateur n'est pas amiral
  if (userGrade !== 'amiral') {
    return null;
  }

  const isTextEmergent = textMode === 'emergent';
  const isImageEmergent = imageMode === 'emergent';
  const serviceName = SERVICE_NAMES[serviceId] || serviceId;

  // Infos pour le dialogue de confirmation
  const getConfirmInfo = () => {
    if (!pendingChange) return {};
    
    const isText = pendingChange.type === 'text';
    const currentMode = isText ? textMode : imageMode;
    const newMode = pendingChange.newMode;
    
    return {
      title: isText ? 'Changer le mode LLM (Texte)' : 'Changer le mode Image',
      currentProvider: isText 
        ? (currentMode === 'emergent' ? 'Claude/Gemini (Emergent)' : 'Anthropic/OpenAI (Direct)')
        : (currentMode === 'emergent' ? 'Gemini (Emergent)' : 'OpenAI gpt-image-1.5 (Direct)'),
      newProvider: isText
        ? (newMode === 'emergent' ? 'Claude/Gemini (Emergent)' : 'Anthropic/OpenAI (Direct)')
        : (newMode === 'emergent' ? 'Gemini (Emergent)' : 'OpenAI gpt-image-1.5 (Direct)'),
      warning: newMode === 'direct'
        ? "⚠️ Le mode Direct utilise vos clés API personnelles."
        : "⚠️ Le mode Emergent utilise la clé Universal Key (vérifiez votre solde)."
    };
  };

  const confirmInfo = getConfirmInfo();

  return (
    <>
      <div 
        className={`inline-flex items-center gap-3 px-3 py-2 bg-red-950/60 rounded-lg border-2 border-red-500/70 shadow-lg shadow-red-500/20 ${className}`}
        data-testid="llm-toggle-container"
      >
        {/* Toggle Texte */}
        <div className="flex items-center gap-2">
          <Type className="w-4 h-4 text-red-300" />
          <span className="text-xs font-bold text-red-300 uppercase tracking-wide">Texte</span>
          <button
            onClick={() => handleToggleClick('text')}
            disabled={isLoading}
            data-testid="llm-text-toggle-button"
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold transition-all border ${
              isTextEmergent 
                ? 'bg-amber-600 text-white border-amber-400 hover:bg-amber-500' 
                : 'bg-blue-600 text-white border-blue-400 hover:bg-blue-500'
            } ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:scale-105'}`}
            title={`Texte: ${textMode}. Cliquer pour basculer.`}
          >
            {isLoading ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : isTextEmergent ? (
              <Zap className="w-3 h-3" />
            ) : (
              <Cloud className="w-3 h-3" />
            )}
            <span>{isTextEmergent ? 'Emergent' : 'Direct'}</span>
          </button>
        </div>

        {/* Séparateur */}
        <div className="w-px h-6 bg-red-500/50"></div>

        {/* Toggle Images */}
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-red-300" />
          <span className="text-xs font-bold text-red-300 uppercase tracking-wide">Image</span>
          <button
            onClick={() => handleToggleClick('image')}
            disabled={isLoading}
            data-testid="llm-image-toggle-button"
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold transition-all border ${
              isImageEmergent 
                ? 'bg-amber-600 text-white border-amber-400 hover:bg-amber-500' 
                : 'bg-blue-600 text-white border-blue-400 hover:bg-blue-500'
            } ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:scale-105'}`}
            title={`Image: ${imageMode}. Cliquer pour basculer.`}
          >
            {isLoading ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : isImageEmergent ? (
              <Zap className="w-3 h-3" />
            ) : (
              <Cloud className="w-3 h-3" />
            )}
            <span>{isImageEmergent ? 'Emergent' : 'Direct'}</span>
          </button>
        </div>
      </div>

      {/* Popup de confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="bg-slate-900 border-red-500/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              {confirmInfo.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300 space-y-3">
              <p>
                Vous êtes sur le point de changer le mode pour le service 
                <span className="font-bold text-white"> {serviceName}</span>.
              </p>
              <div className="flex items-center justify-center gap-4 py-3">
                <div className="px-3 py-2 rounded-lg border bg-slate-700/50 border-slate-600 text-slate-300">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <span className="font-medium text-sm">{confirmInfo.currentProvider}</span>
                  </div>
                  <span className="text-xs text-slate-400">(actuel)</span>
                </div>
                <span className="text-slate-500">→</span>
                <div className="px-3 py-2 rounded-lg border bg-red-500/20 border-red-500 text-red-300">
                  <div className="flex items-center gap-2">
                    <Cloud className="w-4 h-4" />
                    <span className="font-medium text-sm">{confirmInfo.newProvider}</span>
                  </div>
                  <span className="text-xs">(nouveau)</span>
                </div>
              </div>
              <p className="text-amber-400 text-sm">
                {confirmInfo.warning}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleCancel}
              className="bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className="bg-red-600 hover:bg-red-500 border-red-500 text-white font-bold"
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ServiceLLMToggle;
